import { DomainError } from "./errors";
import { roundHalfAwayFromZero } from "./money";
import { assertVatRate, netFromGross, vatFromNet } from "./vat";

export type ItemKind = "part" | "labour" | "other";

/**
 * Workshops quote either net prices (trade customers) or gross prices
 * (private customers, e.g. "1500 Ft"). The price mode decides which amount is
 * exact; the other is derived so that net + VAT = gross always holds.
 */
export type PriceMode = "net" | "gross";

export interface WorkOrderItemInput {
  kind: ItemKind;
  /** Pieces, litres or hours; up to 3 decimals. */
  quantity: number;
  /** Unit price in minor units, net or gross according to priceMode. */
  unitPrice: number;
  priceMode: PriceMode;
  vatRate: number;
  /** Credit items (e.g. discount, returned part) may have a negative unit price. */
  isCredit?: boolean;
}

export interface ItemTotals {
  net: number;
  vat: number;
  gross: number;
}

export interface WorkOrderTotals extends ItemTotals {
  /** Per VAT rate summary, as required on Hungarian invoices. */
  byVatRate: Array<{ vatRate: number } & ItemTotals>;
  byKind: Record<ItemKind, ItemTotals>;
}

const QUANTITY_DECIMALS = 3;

export function validateItem(item: WorkOrderItemInput): void {
  if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
    throw new DomainError("INVALID_QUANTITY", String(item.quantity));
  }
  const q = item.quantity * 10 ** QUANTITY_DECIMALS;
  if (Math.abs(q - Math.round(q)) > 1e-6) {
    throw new DomainError("INVALID_QUANTITY", `more than ${QUANTITY_DECIMALS} decimals`);
  }
  if (!Number.isInteger(item.unitPrice) || (item.unitPrice < 0 && !item.isCredit)) {
    throw new DomainError("INVALID_PRICE", String(item.unitPrice));
  }
  assertVatRate(item.vatRate);
}

/** Totals of one line, computed per line (the method Számlázz.hu also uses). */
export function itemTotals(item: WorkOrderItemInput): ItemTotals {
  validateItem(item);
  const lineAmount = roundHalfAwayFromZero(item.quantity * item.unitPrice);
  if (item.priceMode === "gross") {
    const net = netFromGross(lineAmount, item.vatRate);
    return { net, vat: lineAmount - net, gross: lineAmount };
  }
  const vat = vatFromNet(lineAmount, item.vatRate);
  return { net: lineAmount, vat, gross: lineAmount + vat };
}

const zero = (): ItemTotals => ({ net: 0, vat: 0, gross: 0 });
const add = (a: ItemTotals, b: ItemTotals): ItemTotals => ({
  net: a.net + b.net,
  vat: a.vat + b.vat,
  gross: a.gross + b.gross,
});

export function workOrderTotals(items: readonly WorkOrderItemInput[]): WorkOrderTotals {
  const byRate = new Map<number, ItemTotals>();
  const byKind: Record<ItemKind, ItemTotals> = { part: zero(), labour: zero(), other: zero() };
  let total = zero();
  for (const item of items) {
    const t = itemTotals(item);
    total = add(total, t);
    byKind[item.kind] = add(byKind[item.kind], t);
    byRate.set(item.vatRate, add(byRate.get(item.vatRate) ?? zero(), t));
  }
  const byVatRate = [...byRate.entries()]
    .sort(([a], [b]) => b - a)
    .map(([vatRate, t]) => ({ vatRate, ...t }));
  return { ...total, byVatRate, byKind };
}
